import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ref, get, update } from "firebase/database";
import { database } from "../../../firebase/firebase-config";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

export const GsmEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gsmData, setGsmData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    navigate("/overzicht");
  };

  useEffect(() => {
    const fetchGsmData = async () => {
      try {
        const docRef = ref(database, `GSM/${id}`);
        const snapshot = await get(docRef);
        if (snapshot.exists()) {
          setGsmData(snapshot.val());
        } else {
          setError("No such document!");
        }
      } catch (error) {
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchGsmData();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGsmData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const docRef = ref(database, `GSM/${id}`);
      await update(docRef, gsmData);
      handleClickOpen(); // Show confirmation dialog
    } catch (error) {
      console.error("Failed to update GSM:", error);
      setError("Failed to update GSM");
    }
  };

  if (loading) {
    return (
      <Box
        height="100vh"
        display="flex"
        flexDirection="column"
        justifyContent={"center"}
        alignContent={"center"}
        alignItems={"center"}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
    >
      <Box
        component="form"
        onSubmit={handleFormSubmit}
        sx={{ padding: 15, width: "100%", maxWidth: 600 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          Edit GSM
        </Typography>
        <TextField
          variant="outlined"
          label="Brand"
          name="brand"
          value={gsmData?.brand || ""}
          onChange={handleInputChange}
          required
          sx={{ marginBottom: 2, width: "100%" }}
        />
        <TextField
          variant="outlined"
          label="Model"
          name="model"
          value={gsmData?.model || ""}
          onChange={handleInputChange}
          required
          sx={{ marginBottom: 2, width: "100%" }}
        />
        <TextField
          variant="outlined"
          label="Date IN"
          name="Date_IN"
          value={gsmData?.Date_IN || ""}
          onChange={handleInputChange}
          required
          sx={{ marginBottom: 2, width: "100%" }}
        />
        <TextField
          variant="outlined"
          label="Date OUT"
          name="Date_OUT"
          value={gsmData?.Date_OUT || ""}
          onChange={handleInputChange}
          sx={{ marginBottom: 2, width: "100%" }}
        />
        <TextField
          variant="outlined"
          label="IMEI Number"
          name="IMEI_number"
          value={gsmData?.IMEI_number || ""}
          onChange={handleInputChange}
          required
          sx={{ marginBottom: 2, width: "100%" }}
        />
        <TextField
          variant="outlined"
          label="SIN Number"
          name="SIN_Number"
          value={gsmData?.SIN_Number || ""}
          onChange={handleInputChange}
          required
          sx={{ marginBottom: 2, width: "100%" }}
        />
        <TextField
          variant="outlined"
          label="PV Number"
          name="PV_number"
          value={gsmData?.PV_number || ""}
          onChange={handleInputChange}
          required
          helperText="AN.??.L3.?????/20??"
          sx={{ marginBottom: 2, width: "100%" }}
        />
        <TextField
          variant="outlined"
          label="SIM Code"
          name="SIM_code"
          value={gsmData?.SIM_code || ""}
          onChange={handleInputChange}
          required
          helperText="4 digits"
          sx={{ marginBottom: 2, width: "100%" }}
        />
        <TextField
          variant="outlined"
          label="Unlock Code"
          name="Unlock_code"
          value={gsmData?.Unlock_code || ""}
          onChange={handleInputChange}
          required
          helperText="digits or pattern"
          sx={{ marginBottom: 2, width: "100%" }}
        />
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate("/gsm")}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            sx={{ marginRight: 2 }}
          >
            Save Changes
          </Button>
        </Box>
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </Box>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{"GSM Updated Successfully"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The GSM has been updated successfully.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GsmEdit;
